import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { X } from 'lucide-react'
import api, { departmentApi, governanceApi } from '../../services/api'
import { HeadSelect, type HeadCandidate } from '../../pages/admin/HeadSelect'
import {
  SURFACE_OPTIONS,
  type GovernanceBarrierKind,
  type GovernanceRule,
  type GovernanceRuleAction,
  type GovernanceRuleType,
  type GovernanceSurface,
} from './types'

interface MemberRow {
  userId: string
  user: {
    id: string
    email: string
    firstName?: string
    lastName?: string | null
    position?: string | null
    avatarUrl?: string | null
  } | null
}

interface DeptOption {
  id: string
  name: string
}

function memberDisplayName(u: MemberRow['user']): string {
  if (!u) return ''
  const full = [u.firstName, u.lastName].filter(Boolean).join(' ').trim()
  return full || u.email || ''
}

// Server 4xx bodies are either a plain string ({ error: 'message' }, the
// hand-written validators in routes/governance.ts) or a zod issues array
// ({ error: parsed.error.issues }, schema.safeParse failures) — normalize
// both into one human string instead of rendering "[object Object]".
function extractErrorMessage(err: any, fallback: string): string {
  const raw = err?.response?.data?.error
  if (typeof raw === 'string' && raw.trim()) return raw
  if (Array.isArray(raw) && raw.length > 0) {
    const first = raw[0]
    if (typeof first === 'string') return first
    if (first?.message) return String(first.message)
  }
  return fallback
}

/** Create/edit form for a governance rule. Shared by the server-wide Policies
 *  tab (Task 8, departmentId=null) and the per-department page (Task 9,
 *  departmentId set) — self-contained: it fetches its own member/department
 *  candidate lists from `serverId` rather than depending on parent state. */
/** Prefill for a NEW rule (suggested-rule presets). Unlike `initial`, a preset
 *  does not switch the form into edit/PATCH mode — submit still creates. */
export interface RulePreset {
  name?: string
  aiDescription?: string
  keywords?: string[]
  action?: GovernanceRuleAction
  surfaces?: GovernanceSurface[]
}

export function RuleForm({
  serverId,
  departmentId = null,
  type,
  initial,
  preset,
  onSaved,
  onCancel,
}: {
  serverId: string
  departmentId?: string | null
  type: GovernanceRuleType
  initial?: GovernanceRule
  preset?: RulePreset
  onSaved: () => void
  onCancel: () => void
}) {
  const [name, setName] = useState(initial?.name ?? preset?.name ?? '')
  const [surfaces, setSurfaces] = useState<GovernanceSurface[]>(initial?.surfaces ?? preset?.surfaces ?? [])
  const [recipientIds, setRecipientIds] = useState<string[]>(initial?.recipientIds ?? [])
  const [aiDescription, setAiDescription] = useState(initial?.aiDescription ?? preset?.aiDescription ?? '')
  const [keywords, setKeywords] = useState<string[]>(initial?.keywords ?? preset?.keywords ?? [])
  const [keywordDraft, setKeywordDraft] = useState('')
  const [action, setAction] = useState<GovernanceRuleAction>(initial?.action ?? preset?.action ?? 'notify')
  const [barrierAKind, setBarrierAKind] = useState<GovernanceBarrierKind>(initial?.barrierA?.kind ?? 'user')
  const [barrierAId, setBarrierAId] = useState(initial?.barrierA?.id ?? '')
  const [barrierBKind, setBarrierBKind] = useState<GovernanceBarrierKind>(initial?.barrierB?.kind ?? 'user')
  const [barrierBId, setBarrierBId] = useState(initial?.barrierB?.id ?? '')

  const [members, setMembers] = useState<MemberRow[]>([])
  const [departments, setDepartments] = useState<DeptOption[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  // Recipients + barrier "user" side both need the member list — fetch once,
  // regardless of type (recipientIds is a common field on every rule type).
  useEffect(() => {
    let cancelled = false
    api.get(`/servers/${serverId}/members`).then((res) => { if (!cancelled) setMembers(res.data) }).catch(() => {})
    return () => { cancelled = true }
  }, [serverId])

  // Department options only needed for the barrier "dept" side.
  useEffect(() => {
    if (type !== 'barrier') return
    let cancelled = false
    departmentApi.list(serverId).then((res) => { if (!cancelled) setDepartments(res.data) }).catch(() => {})
    return () => { cancelled = true }
  }, [serverId, type])

  const candidates: HeadCandidate[] = useMemo(
    () =>
      members
        .filter((m) => m.user)
        .map((m) => ({
          userId: m.userId,
          displayName: memberDisplayName(m.user),
          role: m.user?.position ?? null,
          photo: m.user?.avatarUrl ?? null,
        }))
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [members],
  )

  const toggleSurface = (s: GovernanceSurface) => {
    setSurfaces((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]))
  }

  const addKeyword = () => {
    const trimmed = keywordDraft.trim()
    if (!trimmed) return
    setKeywords((prev) => (prev.includes(trimmed) ? prev : [...prev, trimmed]))
    setKeywordDraft('')
  }
  const removeKeyword = (k: string) => setKeywords((prev) => prev.filter((x) => x !== k))

  function validate(): string | null {
    if (!name.trim()) return 'Name is required.'
    if (surfaces.length === 0) return 'Select at least one surface.'
    if (type === 'topic' && !aiDescription.trim()) return 'Describe what is not allowed.'
    if (type === 'keyword' && keywords.length === 0) return 'Add at least one trigger word or phrase.'
    if (type === 'barrier') {
      if (!barrierAId) return 'Select the first side of the barrier.'
      if (!barrierBId) return 'Select the second side of the barrier.'
      if (barrierAKind === barrierBKind && barrierAId === barrierBId) return 'The two sides of a barrier must be different.'
    }
    return null
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const validationError = validate()
    if (validationError) { setFormError(validationError); return }
    setFormError(null)
    setSubmitting(true)
    try {
      if (initial) {
        // PATCH: always send recipientIds explicitly (even empty) so
        // clearing every recipient in the form actually clears it server
        // side — omitting the key would leave the previous value in place.
        const patch: Record<string, unknown> = { name: name.trim(), surfaces, recipientIds }
        if (type === 'topic') { patch.aiDescription = aiDescription.trim(); patch.action = action }
        if (type === 'keyword') { patch.keywords = keywords; patch.action = action }
        if (type === 'barrier') {
          patch.barrierA = { kind: barrierAKind, id: barrierAId }
          patch.barrierB = { kind: barrierBKind, id: barrierBId }
        }
        await governanceApi.updateRule(serverId, initial.id, patch)
      } else {
        // POST: omit recipientIds entirely when empty so a fresh rule keeps
        // the server's null "layer default" semantics instead of an
        // explicit empty array.
        const base = {
          departmentId: departmentId ?? null,
          name: name.trim(),
          surfaces,
          ...(recipientIds.length > 0 ? { recipientIds } : {}),
        }
        if (type === 'topic') {
          await governanceApi.createRule(serverId, { ...base, type, aiDescription: aiDescription.trim(), action })
        } else if (type === 'keyword') {
          await governanceApi.createRule(serverId, { ...base, type, keywords, action })
        } else {
          await governanceApi.createRule(serverId, {
            ...base,
            type,
            barrierA: { kind: barrierAKind, id: barrierAId },
            barrierB: { kind: barrierBKind, id: barrierBId },
          })
        }
      }
      onSaved()
    } catch (err: any) {
      setFormError(extractErrorMessage(err, 'Failed to save rule.'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      {formError && <div className="text-sm bg-destructive/10 text-destructive rounded px-3 py-2">{formError}</div>}

      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. No discussing patient pricing outside Sales"
          className="rule-input w-full"
          autoFocus
        />
      </div>

      {type === 'topic' && (
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">What is not allowed</label>
          <textarea
            value={aiDescription}
            onChange={(e) => setAiDescription(e.target.value)}
            rows={3}
            placeholder="Describe what is NOT allowed — the AI screens messages against this text."
            className="rule-input w-full"
          />
        </div>
      )}

      {type === 'keyword' && (
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Trigger words / phrases</label>
          {keywords.length > 0 && (
            <div className="mb-1.5 flex flex-wrap gap-1.5">
              {keywords.map((k) => (
                <span key={k} className="inline-flex items-center gap-1 rounded-full bg-primary/15 px-2 py-0.5 text-xs font-medium text-primary">
                  {k}
                  <button type="button" onClick={() => removeKeyword(k)} className="text-primary hover:text-foreground transition" aria-label={`Remove ${k}`}>
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
          )}
          <input
            value={keywordDraft}
            onChange={(e) => setKeywordDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addKeyword() } }}
            placeholder="Type a phrase, press Enter"
            className="rule-input w-full"
          />
          <div className="mt-1 text-[11px] text-muted-foreground">Case-insensitive exact-phrase match. At least one required.</div>
        </div>
      )}

      {type === 'barrier' && (
        <div className="grid grid-cols-2 gap-4">
          <BarrierSidePicker
            label="Side A" kind={barrierAKind} id={barrierAId} candidates={candidates} departments={departments}
            onKindChange={(k) => { setBarrierAKind(k); setBarrierAId('') }} onIdChange={setBarrierAId}
          />
          <BarrierSidePicker
            label="Side B" kind={barrierBKind} id={barrierBId} candidates={candidates} departments={departments}
            onKindChange={(k) => { setBarrierBKind(k); setBarrierBId('') }} onIdChange={setBarrierBId}
          />
        </div>
      )}

      <div className="flex items-end gap-6">
        <div>
          <label className="block text-xs font-medium text-muted-foreground mb-1">Action</label>
          {type === 'barrier' ? (
            <select disabled value="block" className="rule-input opacity-70 cursor-not-allowed">
              <option value="block">Block</option>
            </select>
          ) : (
            <select value={action} onChange={(e) => setAction(e.target.value as GovernanceRuleAction)} className="rule-input">
              <option value="notify">Notify</option>
              <option value="block">Block</option>
            </select>
          )}
        </div>
        <div className="flex-1">
          <label className="block text-xs font-medium text-muted-foreground mb-1">Surfaces</label>
          <div className="flex items-center gap-4 h-[30px]">
            {SURFACE_OPTIONS.map((s) => (
              <label key={s.value} className="flex items-center gap-1.5 text-xs text-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={surfaces.includes(s.value)}
                  onChange={() => toggleSurface(s.value)}
                  className="rounded border-border"
                />
                {s.label}
              </label>
            ))}
          </div>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-muted-foreground mb-1">Recipients</label>
        <RecipientsPicker value={recipientIds} candidates={candidates} onChange={setRecipientIds} />
      </div>

      <div className="flex items-center gap-2 pt-1">
        <button
          type="submit"
          disabled={submitting}
          className="px-3 py-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded hover:bg-primary/90 transition disabled:opacity-50"
        >
          {submitting ? 'Saving…' : initial ? 'Save changes' : 'Create rule'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition disabled:opacity-50"
        >
          Cancel
        </button>
      </div>

      <style>{`.rule-input{background:var(--color-muted);color:var(--color-foreground);border-radius:6px;padding:6px 10px;font-size:13px;outline:none;border:1px solid transparent;width:100%}.rule-input:focus{border-color:var(--color-primary)}select.rule-input{width:auto}`}</style>
    </form>
  )
}

function BarrierSidePicker({
  label, kind, id, candidates, departments, onKindChange, onIdChange,
}: {
  label: string
  kind: GovernanceBarrierKind
  id: string
  candidates: HeadCandidate[]
  departments: DeptOption[]
  onKindChange: (k: GovernanceBarrierKind) => void
  onIdChange: (id: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      <div className="flex items-center gap-3 text-xs text-foreground">
        <label className="flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={kind === 'user'} onChange={() => onKindChange('user')} />
          User
        </label>
        <label className="flex items-center gap-1 cursor-pointer">
          <input type="radio" checked={kind === 'dept'} onChange={() => onKindChange('dept')} />
          Department
        </label>
      </div>
      {kind === 'user' ? (
        <HeadSelect value={id || null} candidates={candidates} onChange={(v) => onIdChange(v ?? '')} placeholder="Select user…" width={260} />
      ) : (
        <select value={id} onChange={(e) => onIdChange(e.target.value)} className="rule-input" style={{ width: 260 }}>
          <option value="">Select department…</option>
          {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
      )}
    </div>
  )
}

/** Composes the existing single-user HeadSelect picker into a minimal
 *  multi-select: chips for the current recipients (removable) + a HeadSelect
 *  scoped to the remaining candidates for "add one more". No standalone
 *  multi-select component exists in the codebase yet — this reuses HeadSelect
 *  rather than introducing a new picker widget for v1. */
function RecipientsPicker({
  value, candidates, onChange,
}: {
  value: string[]
  candidates: HeadCandidate[]
  onChange: (ids: string[]) => void
}) {
  const selected = candidates.filter((c) => value.includes(c.userId))
  const remaining = candidates.filter((c) => !value.includes(c.userId))
  return (
    <div className="space-y-1.5">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((c) => (
            <span key={c.userId} className="inline-flex items-center gap-1 text-xs bg-muted text-foreground rounded-full pl-2 pr-1 py-0.5">
              {c.displayName}
              <button
                type="button"
                onClick={() => onChange(value.filter((id) => id !== c.userId))}
                className="text-muted-foreground hover:text-destructive"
                title="Remove"
              >
                <X size={11} />
              </button>
            </span>
          ))}
        </div>
      )}
      <HeadSelect
        value={null}
        candidates={remaining}
        onChange={(id) => { if (id) onChange([...value, id]) }}
        placeholder="+ Add recipient…"
        width={240}
      />
      <div className="text-[11px] text-muted-foreground">Empty = department head / server admins (default).</div>
    </div>
  )
}
